export const FONT_SIZING = {
  xs: {
    fontSize: '0.75rem',
    lineHeight: '1rem',
  },
  sm: {
    fontSize: '0.875rem',
    lineHeight: '1.25rem', // 20px computed (h5)
  },
  md: {
    fontSize: '1rem',
    lineHeight: '1.25rem', // 20px computed (h4)
  },
  lg: {
    fontSize: '1.125rem',
    lineHeight: '1.375rem', // 22px computed (h3)
  },
  xl: {
    fontSize: '1.25rem',
    lineHeight: '1.5rem', // 24px computed (h2)
  },
  xxl: {
    fontSize: '1.5rem',
    lineHeight: '1.875rem', // 30px computed (h1)
  },
  xxxl: {
    fontSize: '2rem',
    lineHeight: '2.25rem', // 36px computed (h1_display)
  },
};

// Serif title ramp used by presentation headers. Mirrors the Figma
// "Title/Serif" styles (Foundation, node 15482:12). Weight is applied
// separately via FONT_WEIGHTS.bold / FONT_WEIGHTS.medium so each entry
// only carries size + line-height.
export const TITLE_SERIF_SIZING = {
  xxl: {
    fontSize: '3rem', // 48px
    lineHeight: '3.875rem', // 62px
  },
  xl: {
    fontSize: '2.25rem', // 36px
    lineHeight: '3rem', // 48px
  },
  l: {
    fontSize: '2rem', // 32px
    lineHeight: '2.75rem', // 44px
  },
  m: {
    fontSize: '1.5rem', // 24px
    lineHeight: '2.125rem', // 34px
  },
  s: {
    fontSize: '1.25rem', // 20px
    lineHeight: '1.75rem', // 28px
  },
  xs: {
    fontSize: '1rem', // 16px
    lineHeight: '1.375rem', // 22px
  },
} as const;

// Bold weight for presentation display headings — font-agnostic (700 is bold
// across the configurable header faces: Romie, Arial, …), not tied to Romie.
export const DISPLAY_HEADING_FONT_WEIGHT = 700;

export const TYPOGRAPHY_SIZING = {
  // Title variants (headers)
  'title-xl': {
    fontSize: '1.5rem', // 24px
    lineHeight: '1.75rem', // 28px
  },
  'title-l': {
    fontSize: '1.25rem', // 20px
    lineHeight: '1.5rem', // 24px
  },
  'title-m': {
    fontSize: '1rem', // 16px
    lineHeight: '1.5rem', // 24px
  },
  'title-s': {
    fontSize: '0.875rem', // 14px
    lineHeight: '1.25rem', // 20px
  },
  'title-xs': {
    fontSize: '0.75rem', // 12px
    lineHeight: '1rem', // 16px
  },
  // Text variants (body text)
  'body-m': {
    fontSize: '1rem', // 16px
    lineHeight: '1.5rem', // 24px
  },
  'body-s': {
    fontSize: '0.875rem', // 14px
    lineHeight: '1.25rem', // 20px
  },
  'body-xs': {
    fontSize: '0.75rem', // 12px
    lineHeight: '1rem', // 16px
  },
  // Uppercase eyebrow label used above values in dense data displays.
  'eyebrow-xs': {
    fontSize: '0.625rem', // 10px
    lineHeight: '1rem', // 16px
  },
};

/**
 * Inline font size overrides for the slide editor's custom text sizing feature.
 * Used by FontSizeNode to apply user-chosen sizes via `[size=N]...[/size]`
 * markdown syntax. Values reference the FONT_SIZING scale above.
 *
 * The slide editor's base paragraph is 1rem (16px) via PresentationTypographyProvider,
 * so 16pt is the default / unwrapped state (selecting 16pt removes the wrapper).
 *
 * Line-heights are unitless and scale proportionally with the computed font-size.
 * They were chosen to match the presentation design spec, not FONT_SIZING line-heights.
 */
export const SLIDE_EDITOR_FONT_SIZES = {
  14: { fontSize: FONT_SIZING.sm.fontSize, lineHeight: '1.43' }, // 20pt line-height
  16: { fontSize: FONT_SIZING.md.fontSize, lineHeight: '1.5' }, // 24pt — default
  18: { fontSize: FONT_SIZING.lg.fontSize, lineHeight: '1.5' }, // 27pt — h2 heading
  20: { fontSize: FONT_SIZING.xl.fontSize, lineHeight: '1.5' }, // 30pt — h1 heading
  24: { fontSize: FONT_SIZING.xxl.fontSize, lineHeight: '1.5' }, // 36pt
} as const;

export type SlideEditorFontSize = keyof typeof SLIDE_EDITOR_FONT_SIZES;

/**
 * Allowed font sizes for diagram text notes (sticky notes). The default
 * text-note font is 12px, so 12pt is the "no wrapper" base state.
 *
 * The smaller sizes (10–18) are the original set; the larger sizes (24, 32,
 * 48) were added to match the tile (diagram node) font-size ramp so the
 * biggest sticky-note option visually matches the biggest node text size.
 *
 * FontSizeNode's `fontSizeToCSS` fallback (`size / 16 rem`) produces the
 * correct CSS for each value, so these don't need an explicit token map —
 * the array is only used by the toolbar dropdown.
 */
export const TEXT_NOTE_FONT_SIZES = [10, 12, 14, 16, 18, 24, 32, 48] as const;
export type TextNoteFontSize = (typeof TEXT_NOTE_FONT_SIZES)[number];
export const TEXT_NOTE_DEFAULT_FONT_SIZE: TextNoteFontSize = 12;

export const FONT_WEIGHTS = {
  light: 300,
  regular: 400,
  medium: 500,
  bold: 600,
};
